import { useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ArrowLeft, Menu } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@reflex/ui/components/ui/button';
import { PanelHeader } from '@reflex/ui/components/layout/PanelHeader';
import { PageHeaderAffordances } from '@reflex/ui/components/layout/PageLayout';
import { PanelTitle, SectionLabel } from '@reflex/ui/components/layout/Typography';
import { PageChromeProvider, usePageChrome } from './page-chrome';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@reflex/ui/components/ui/sheet';
import { KbdCombo } from '@reflex/ui/components/shared/Kbd';
import { useIsDesktop } from '@reflex/ui/hooks/useIsDesktop';
import { cn } from '@reflex/ui/lib/utils';
import { TakeoverFeedbackFooter } from './TakeoverFeedbackFooter';

/**
 * Shared chrome for takeover surfaces (Configure, Inbox, Automations): the
 * back chip with its `esc` hint, the identity header naming exactly where
 * you are, and the aside column that stacks them. One set of components so
 * entering and leaving any dedicated area looks and behaves identically.
 */

// Nav rows carry a 2px accent border before their 10px padding, so their
// icons sit 18px in and their labels 44px in (inside the px-1.5 container).
// The chip (and any search field beside it) use px-3 + the same icon size
// and gap to land on exactly that rail.
const backChipClasses =
  'inline-flex h-7 items-center gap-2.5 rounded-md px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-sidebar-accent/40 hover:text-sidebar-foreground';

export interface BackChipProps {
  label: string;
  to?: string;
  onClick?: () => void;
  testId: string;
}

/**
 * The one back affordance every takeover surface shares: a compact chip with
 * the `esc` hint, because `esc` always does exactly what the chip does.
 */
export function BackChip({ label, to, onClick, testId }: BackChipProps) {
  const content = (
    <>
      <ArrowLeft className="h-4 w-4 shrink-0" />
      {label}
      <KbdCombo keys={['esc']} size="xs" className="ml-0.5 hidden sm:inline-flex" />
    </>
  );
  if (to) {
    return (
      <Link to={to} data-testid={testId} className={backChipClasses}>
        {content}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid={testId} className={backChipClasses}>
      {content}
    </button>
  );
}

/**
 * Square identity tile: the avatar image when set, otherwise the surface's
 * glyph (org building, inbox tray, automation workflow) or an initial for
 * people.
 */
export function IdentityTile({
  avatarUrl,
  icon: Icon,
  initialOf,
}: {
  avatarUrl?: string | null;
  icon?: LucideIcon;
  initialOf?: string;
}) {
  if (avatarUrl) {
    return <img src={avatarUrl} alt="" className="h-8 w-8 shrink-0 rounded-lg object-cover" />;
  }
  if (Icon) {
    return (
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </span>
    );
  }
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold">
      {(initialOf ?? '?').charAt(0).toUpperCase()}
    </span>
  );
}

/**
 * The header that answers "where am I": a strong tile-and-name block with
 * the surface spelled out underneath, so every takeover unmistakably reads
 * as one specific place — yours, one organization's, or a named surface.
 */
export function IdentityHeader({
  tile,
  title,
  subtitle,
  docs,
  actions,
}: {
  tile: React.ReactNode;
  title: string;
  subtitle: string;
  /** Optional docs slug rendered as the PanelTitle docs hint. */
  docs?: string;
  /** Optional trailing controls (e.g. the inbox launch button). */
  actions?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 px-4 pb-4 pt-2.5" data-testid="takeover-identity">
      {tile}
      <div className="min-w-0 flex-1">
        <PanelTitle as="h2" size="sm" docs={docs}>
          {title}
        </PanelTitle>
        <SectionLabel as="span" className="mt-0.5 block normal-case tracking-normal">
          {subtitle}
        </SectionLabel>
      </div>
      {actions}
    </div>
  );
}

/**
 * Outer flex for every takeover shell: column on mobile (header + page),
 * row from `md` up (aside + page).
 */
export const takeoverShellClassName =
  'flex h-full min-h-0 flex-1 flex-col bg-background md:flex-row';

const asideColumnClassName =
  'flex w-60 shrink-0 flex-col border-r border-sidebar-border bg-sidebar';

const DESKTOP_QUERY = '(min-width: 768px)';

/** Open the mobile nav on first paint when entering a takeover below `md`. */
function initialMobileNavOpen(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return !window.matchMedia(DESKTOP_QUERY).matches;
}

function TakeoverAsideBody({
  back,
  header,
  footer,
  children,
  showPinnedFooter,
}: {
  back: BackChipProps;
  header: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  /**
   * Desktop-only. Mobile reaches the footer items through the More sheet;
   * the utility icons have no mobile surface anywhere.
   */
  showPinnedFooter: boolean;
}) {
  return (
    <>
      <div className="px-1.5 pt-3">
        <BackChip {...back} />
      </div>
      {header}
      {children}
      {footer}
      {showPinnedFooter && (
        <div className="mt-auto flex shrink-0 flex-col">
          <TakeoverFeedbackFooter />
        </div>
      )}
    </>
  );
}

/**
 * The aside column every takeover surface opens with: back chip on top,
 * identity header underneath, then the surface's own nav. Pass `page` and
 * `pageTitle` from a shell so mobile gets a hamburger header (like the
 * agent stream) instead of stacking the aside above the page. On phones
 * the sheet opens on entry so the user can pick a destination other than
 * the restored default. Restore redirects keep it open; choosing a row
 * or dismissing closes it. Plugin utility icons (Chief) and
 * `takeover-footer` items (Send feedback) pin under the nav on desktop;
 * on mobile the footer items live in the More sheet and the utility
 * icons have no surface.
 */
export function TakeoverAside(props: {
  back: BackChipProps;
  header: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  /** Page content. When set, this component owns the full shell layout. */
  page?: React.ReactNode;
  /** Current destination label shown in the mobile header. */
  pageTitle?: string;
}) {
  return (
    <PageChromeProvider>
      <TakeoverAsideInner {...props} />
    </PageChromeProvider>
  );
}

function TakeoverAsideInner({
  back,
  header,
  footer,
  children,
  page,
  pageTitle,
}: {
  back: BackChipProps;
  header: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  page?: React.ReactNode;
  pageTitle?: string;
}) {
  const isDesktop = useIsDesktop();
  const [navOpen, setNavOpen] = useState(initialMobileNavOpen);
  const chrome = usePageChrome();

  const body = (
    <TakeoverAsideBody back={back} header={header} footer={footer} showPinnedFooter={isDesktop}>
      {children}
    </TakeoverAsideBody>
  );

  if (page === undefined) {
    return <aside className={asideColumnClassName}>{body}</aside>;
  }

  return (
    <>
      {isDesktop ? (
        <aside className={asideColumnClassName}>{body}</aside>
      ) : (
        <Sheet open={navOpen} onOpenChange={setNavOpen}>
          <SheetContent
            side="left"
            className={cn(asideColumnClassName, 'h-full max-w-[85vw] p-0')}
            data-testid="takeover-mobile-nav"
            onClick={(event) => {
              if ((event.target as HTMLElement).closest('a[href]')) {
                setNavOpen(false);
              }
            }}
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Navigation</SheetTitle>
              <SheetDescription>Section destinations</SheetDescription>
            </SheetHeader>
            {body}
          </SheetContent>
        </Sheet>
      )}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {!isDesktop && (
          <PanelHeader
            className="h-11 gap-0 px-0"
            leftClassName="gap-0"
            data-testid="takeover-mobile-header"
            left={
              <>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="h-11 w-11 shrink-0"
                  onClick={() => setNavOpen(true)}
                  aria-label="Open sidebar"
                >
                  <Menu className="h-5 w-5" />
                </Button>
                {chrome?.back ? (
                  <Button asChild variant="ghost" size="icon-sm" className="h-11 w-8 shrink-0">
                    <Link
                      to={chrome.back.to}
                      aria-label={chrome.back.ariaLabel ?? 'Go back'}
                      data-testid="takeover-mobile-back"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </Link>
                  </Button>
                ) : null}
                <PanelTitle as="h1" size="sm" className="truncate">
                  {chrome?.title ?? pageTitle}
                </PanelTitle>
              </>
            }
            right={
              chrome?.docs || chrome?.description ? (
                <PageHeaderAffordances docs={chrome.docs} description={chrome.description} />
              ) : undefined
            }
          />
        )}
        <div
          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
          data-mobile-chrome-title=""
        >
          {page}
        </div>
      </div>
    </>
  );
}
